import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Phone, FileText, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import PDFModal from './PDFModal';
import './EquipmentPage.css';

const EquipmentPage = ({ data }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [pdfModal, setPdfModal] = useState(null);
  const touchStartX = useRef(null);

  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hola, me interesa información sobre ${data.title}`;

  const getImageBasePath = () => {
    if (data.category === 'aceite-vegetal') return '/assets/tecnal/av';
    if (data.category === 'alimentacion-animal') return '/assets/tecnal/aa';
    if (data.category === 'almacenamiento-de-granos') return '/assets/tecnal/ag';
    if (data.category === 'movimiento-portuario') return '/assets/tecnal/mp';
    if (data.category === 'etanol-de-maiz') return '/assets/tecnal/em';
    if (data.category === 'referencias') return '/assets/tecnal/referencias';
    return '/assets/tecnal';
  };

  const basePath = getImageBasePath();
  const images = data.images || [];
  const hasImages = images.length > 0;

  const nextImage = () => setSelectedImage((prev) => (prev + 1) % images.length);
  const prevImage = () => setSelectedImage((prev) => (prev - 1 + images.length) % images.length);

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  // Keyboard navigation + body scroll lock for lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowRight') setSelectedImage(prev => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setSelectedImage(prev => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isLightboxOpen, images.length]);

  return (
    <div className="equipment-page" data-testid="equipment-page">
      {/* Hero Section */}
      <section className="equipment-hero">
        <div className="equipment-hero-content">
          <h1 className="equipment-hero-title" data-testid="equipment-title">{data.title}</h1>
          <p className="equipment-hero-description">{data.description}</p>
        </div>
      </section>

      {/* Gallery Section */}
      {hasImages && (
        <section className="equipment-gallery-section">
          <div className="container">
            <h2 className="section-title">Galería de imágenes</h2>

            <div className="gallery-layout">
              {/* Featured Image */}
              <div className="gallery-featured">
                <div
                  className="gallery-featured-wrap"
                  onClick={() => openLightbox(selectedImage)}
                  onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                  onTouchEnd={(e) => {
                    if (touchStartX.current === null || images.length <= 1) return;
                    const diff = touchStartX.current - e.changedTouches[0].clientX;
                    if (Math.abs(diff) > 50) {
                      if (diff > 0) nextImage(); else prevImage();
                    }
                    touchStartX.current = null;
                  }}
                  data-testid="equipment-main-image"
                >
                  <img
                    key={selectedImage}
                    src={`${basePath}/${images[selectedImage]}`}
                    alt={`${data.title} - Imagen ${selectedImage + 1}`}
                    loading="lazy"
                    className="gallery-featured-image"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/800x500?text=${encodeURIComponent(data.title)}`;
                    }}
                  />
                  <div className="gallery-featured-overlay">
                    <ZoomIn size={30} strokeWidth={1.5} />
                    <span>Ver en pantalla completa</span>
                  </div>
                </div>
                {images.length > 1 && (
                  <div className="gallery-featured-counter">
                    {selectedImage + 1} <span>/ {images.length}</span>
                  </div>
                )}
              </div>

              {/* Vertical Thumbnail Strip */}
              {images.length > 1 && (
                <div className="gallery-thumb-strip" data-testid="equipment-thumbnails">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      className={`gallery-thumb${index === selectedImage ? ' active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <img
                        src={`${basePath}/${img}`}
                        alt={`${data.title} - Miniatura ${index + 1}`}
                        loading="lazy"
                        className="gallery-thumb-img"
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/150x100?text=${index + 1}`;
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* No Images Placeholder */}
      {!hasImages && (
        <section className="equipment-gallery-section">
          <div className="container">
            <div className="no-images-placeholder">
              <p>Imágenes próximamente disponibles</p>
            </div>
          </div>
        </section>
      )}

      {/* Technical Description Section */}
      <section className="equipment-description-section">
        <div className="container">
          <h2 className="section-title">Descripción técnica</h2>
          {data.technicalData ? (
            <div className="tech-data-wrapper" data-testid="equipment-description">
              {data.technicalData.intro && (
                <p className="tech-intro">{data.technicalData.intro}</p>
              )}
              <div className="tech-sections-grid">
                {data.technicalData.sections.map((section, idx) => (
                  <div
                    key={idx}
                    className={`tech-section tech-section--${section.type || 'default'}${section.fullWidth ? ' tech-section--full' : ''}`}
                  >
                    <h3 className="tech-section-title">{section.title}</h3>
                    <ul className="tech-section-list">
                      {section.items.map((item, i) => (
                        <li key={i} className="tech-section-item">{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="equipment-tech-description" data-testid="equipment-description">
              <p>{data.technicalDescription || 'Descripción técnica próximamente disponible. Contactenos para más información sobre este equipo.'}</p>
            </div>
          )}
        </div>
      </section>

      {/* PDF Button */}
      {data.pdfHref && (
        <div className="equipment-pdf-section">
          <button
            className="equipment-pdf-button"
            onClick={() => setPdfModal({ url: data.pdfHref, title: data.title })}
          >
            <FileText size={18} />
            Ver catálogo PDF
          </button>
        </div>
      )}

      {/* CTA Section */}
      <section className="equipment-cta-section">
        <div className="container">
          <h2 className="equipment-cta-title">
            ¿Interesado en este equipo?
          </h2>
          <p className="equipment-cta-subtitle">
            Contactanos para obtener más información técnica y cotización
          </p>
          <Button
            size="lg"
            className="equipment-cta-button"
            onClick={() => window.open(whatsappLink, '_blank')}
            data-testid="equipment-contact-button"
          >
            <Phone className="mr-2" size={20} />
            Solicitar información
          </Button>
        </div>
      </section>

      {pdfModal && (
        <PDFModal
          pdfUrl={pdfModal.url}
          title={pdfModal.title}
          onClose={() => setPdfModal(null)}
        />
      )}

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)} data-testid="lightbox">
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>

            <div className="lightbox-image-area">
              {images.length > 1 && (
                <button
                  className="lightbox-nav prev"
                  onClick={() => setSelectedImage(prev => (prev - 1 + images.length) % images.length)}
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft size={28} />
                </button>
              )}
              <img
                key={`lb-${selectedImage}`}
                src={`${basePath}/${images[selectedImage]}`}
                alt={`${data.title} - Imagen ${selectedImage + 1}`}
                className="lightbox-image"
              />
              {images.length > 1 && (
                <button
                  className="lightbox-nav next"
                  onClick={() => setSelectedImage(prev => (prev + 1) % images.length)}
                  aria-label="Siguiente imagen"
                >
                  <ChevronRight size={28} />
                </button>
              )}
            </div>

            <div className="lightbox-footer">
              <span className="lightbox-counter">{selectedImage + 1} / {images.length}</span>
              {images.length > 1 && (
                <div className="lightbox-thumbs">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      className={`lightbox-thumb${index === selectedImage ? ' active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <img
                        src={`${basePath}/${img}`}
                        alt=""
                        className="lightbox-thumb-img"
                        onError={(e) => { e.target.src = `https://via.placeholder.com/80x60?text=${index + 1}`; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentPage;
